<script setup lang="ts">
import type { SubjectCode } from '~/models/Subject'
import type { Task } from '~/models/AssignTask'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { faArrowRight, faEye, faLock } from '@fortawesome/duotone-regular-svg-icons'
import useArrayUtils from '~/utils/arrayUtils'
import useGroupsStore from '~/stores/groups'
import useAuthStore from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import { TaskStatus } from '~/models/AssignTask'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useGradeString from '~/composables/useGradeString'
import { defaultMode, defaultSubject, getReportTag, supportedSubjects } from '~/composables/useAssignTags'
import Button from '~/components/ui/Button.vue'
import EditAssignTaskDialog from '~/components/products/exams/assignment/EditAssignTaskDialog.vue'
import AssignStudentsDialog from '~/components/products/exams/assignment/AssignStudentsDialog.vue'

const { task } = defineProps<{ task: Task }>()

const { t } = useI18n()
const { findGroupById } = useGroupsStore()
const { createGradeString } = useGradeString()
const { isTeacher, isStudent } = storeToRefs(useAuthStore())
const { buildAssignedTaskUrl } = useUrlBuilder()
const { intersect } = useArrayUtils()

const groupName = computed(() => task.groups?.length ? findGroupById(task.groups[0])?.name : null)
const subjectCode = computed((): SubjectCode => task.subjects?.length ? task.subjects[0] : defaultSubject)

const subtitle = computed(() => {
  const grades = task.grades ? createGradeString(task.grades) : ''
  return [grades, t('termExam.title')].filter(Boolean).join(' | ')
})

const showReport = computed(() => !!task.users?.length
  && intersect(supportedSubjects, task.subjects ?? []).length > 0)

const reportRoute = computed(() => ({
  name: 'term-exam-report',
  query: {
    groupId: task.groups?.[0] ?? '',
    grade: task.grades?.[0] ?? '',
    subject: subjectCode.value,
    context: getReportTag(subjectCode.value),
    mode: defaultMode
  }
}))

const taskIsOpen = computed(() => task.status === TaskStatus.Open)
</script>

<template>
  <article class="group relative flex size-full flex-col flex-wrap items-start justify-start gap-2 rounded-xl bg-white p-3 shadow-md transition sm:flex-row sm:items-center">
    <div
      class="absolute top-0 right-0 flex size-8 shrink-0 flex-col items-center justify-center gap-1 rounded-lg text-xs sm:relative sm:size-14 sm:text-base"
      :class="taskIsOpen ? 'bg-green-5' : 'bg-coral-5'"
    >
      <font-awesome-icon
        :class="taskIsOpen ? 'text-green-40' : 'text-coral-40'"
        :icon="taskIsOpen ? faEye : faLock"
        :beat="taskIsOpen"
        style="--fa-animation-duration: 1500ms;"
      />
      <span
        class="hidden text-xs font-semibold sm:block"
        :class="taskIsOpen ? 'text-green-50' : 'text-coral-50'"
      >
        {{ taskIsOpen ? t('assign.shared') : t('assign.closed') }}
      </span>
    </div>
    <div class="flex">
      <div
        class="flex shrink-0 flex-col gap-1"
        :class="groupName && 'border-r border-blue-10 pr-4'"
      >
        <p
          class="text-gray-40 text-xs font-medium tracking-wider uppercase"
          v-text="subtitle"
        />
        <h3
          class="font-bold text-gray-50 md:text-lg"
          v-text="task.name"
        />
      </div>
      <div
        v-if="groupName"
        class="flex flex-col gap-1.5 pl-4"
      >
        <span
          class="text-gray-40 text-xs font-medium tracking-wider uppercase"
          v-text="t('groups.groupName')"
        />
        <p
          class="line-clamp-2 text-sm font-bold text-gray-50"
          v-text="groupName"
        />
      </div>
    </div>
    <div
      v-if="isTeacher"
      class="flex items-center gap-2 md:ml-auto"
    >
      <Button
        v-if="showReport"
        :to="reportRoute"
        color="orange"
        shade="light"
      >
        {{ t('report.button') }}
      </Button>
      <EditAssignTaskDialog
        v-if="taskIsOpen"
        :task
      />
      <AssignStudentsDialog
        v-else
        :task
      />
    </div>
    <Button
      v-if="isTeacher || (isStudent && task.status === TaskStatus.Open)"
      :to="buildAssignedTaskUrl(task)"
      :shade="isTeacher ? 'light' : 'dark'"
      :size="isTeacher ? 'md' : 'lg'"
      color="blue"
      :class="{ 'ml-auto': isStudent }"
      :data-pendo="PendoFeature.TermExam.GoToTask"
    >
      <font-awesome-icon v-if="isStudent" :icon="faArrowRight" />
      {{ isTeacher ? t('assign.seeExam') : t('assign.goToExam') }}
    </Button>
  </article>
</template>
